import { SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

/** Strip dangerous constructs from theme CSS injected into preview documents. */
export const sanitizeThemeCss = (css: string): string => {
    if (!css) {
        return '';
    }
    return css
        .replace(/@import\b[^;]+;?/gi, '')
        .replace(/expression\s*\(/gi, '')
        .replace(/javascript\s*:/gi, '')
        .replace(/behavior\s*:/gi, '');
};

/**
 * Strip executable XSS only — preserves email layout, inline styles, and external CDN URLs.
 * Use for sandboxed iframe previews and email editor save paths.
 */
export const stripMalwareFromHtml = (html: string): string => {
    if (!html) {
        return '';
    }

    return String(html)
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/\s(on\w+|formaction|xmlns)\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
        .replace(/<base\b[^>]*>/gi, '')
        .replace(/<meta\b[^>]*http-equiv\s*=\s*["']?refresh["']?[^>]*>/gi, '')
        .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
        .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
        .replace(/<embed\b[^>]*>/gi, '');
};

/** Alias for editor/API save paths — strips malware vectors, keeps email HTML intact. */
export const sanitizeHtmlForSave = stripMalwareFromHtml;

/**
 * Strip XSS vectors before binding to [innerHTML].
 * Cross-origin URLs are not domain-filtered; Angular DomSanitizer + pipe handle unsafe markup.
 */
export const sanitizeHtmlContent = (html: string): string => stripMalwareFromHtml(html);

/**
 * Preview mapping snippets use `<a id>` / `name` / `style` / `javascript:void(0)`.
 * Angular's HTML sanitizer strips those attributes, which breaks Map clicks.
 * Example: {{header_1:<a class="text-underline cursor-pointer link" id="image:header_1">Custom Value</a>}}
 */
const MAPPING_BLOCK_REGEX = /\{\{([^{}<]{1,128}):<a\b([^>]*)>([\s\S]*?)<\/a>\}\}/gi;
const MAPPING_PLACEHOLDER_PREFIX = '__MSG91_SAFE_MAP_';
const SAFE_MAPPING_TOKEN = /^[\w.:-]+$/;
const SAFE_MAPPING_CLASS = /^[\w\s:-]+$/;
const SAFE_VOID_HREF = /^javascript:void\(0\);?$/i;
const ATTR_REGEX = /([^\s=/>]+)(\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/gi;

const parseHtmlAttributes = (raw: string): Record<string, string> => {
    const attrs: Record<string, string> = {};
    const source = raw.trim();
    if (!source) {
        return attrs;
    }

    ATTR_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = ATTR_REGEX.exec(source))) {
        attrs[match[1].toLowerCase()] = match[3] ?? match[4] ?? match[5] ?? '';
    }
    return attrs;
};

const rebuildSafeMappingBlock = (varName: string, rawAttrs: string, inner: string): string | null => {
    if (!SAFE_MAPPING_TOKEN.test(varName)) {
        return null;
    }

    const attrs = parseHtmlAttributes(rawAttrs);
    const className = attrs.class ?? '';
    const id = attrs.id ?? '';
    const name = attrs.name ?? '';
    const href = attrs.href ?? '';
    const style = attrs.style ? sanitizeThemeCss(attrs.style) : '';

    if (className && !SAFE_MAPPING_CLASS.test(className)) {
        return null;
    }
    if (id && !SAFE_MAPPING_TOKEN.test(id)) {
        return null;
    }
    if (name && !SAFE_MAPPING_TOKEN.test(name)) {
        return null;
    }
    if (href && !SAFE_VOID_HREF.test(href)) {
        return null;
    }
    if (!id && !name && !href) {
        return null;
    }

    const attrParts: string[] = [];
    if (className) {
        attrParts.push(`class="${escapeHtml(className)}"`);
    }
    if (id) {
        attrParts.push(`id="${escapeHtml(id)}"`);
    }
    if (name) {
        attrParts.push(`name="${escapeHtml(name)}"`);
    }
    if (href) {
        attrParts.push('href="javascript:void(0)"');
    }
    if (style) {
        attrParts.push(`style="${escapeHtml(style)}"`);
    }

    const label = escapeHtml(String(inner).replace(/<[^>]*>/g, ''));
    return `{{${varName}:<a ${attrParts.join(' ')}>${label}</a>}}`;
};

/** Pull safe `{{var:<a …>…</a>}}` mapping blocks out so Angular sanitizer cannot strip `id`/`name`. */
export const extractSafeMappingBlocks = (html: string): { html: string; blocks: string[] } => {
    const blocks: string[] = [];
    MAPPING_BLOCK_REGEX.lastIndex = 0;
    const next = String(html).replace(MAPPING_BLOCK_REGEX, (full, varName: string, rawAttrs: string, inner: string) => {
        const rebuilt = rebuildSafeMappingBlock(varName, rawAttrs, inner);
        if (!rebuilt) {
            return full;
        }
        const token = `${MAPPING_PLACEHOLDER_PREFIX}${blocks.length}__`;
        blocks.push(rebuilt);
        return token;
    });
    return { html: next, blocks };
};

export const restoreSafeMappingBlocks = (html: string, blocks: string[]): string => {
    if (!blocks.length) {
        return html;
    }
    let next = html;
    for (let i = blocks.length - 1; i >= 0; i--) {
        next = next.replaceAll(`${MAPPING_PLACEHOLDER_PREFIX}${i}__`, blocks[i]);
    }
    return next;
};

/** Full sanitization for TS paths in the app — strip malware then Angular DomSanitizer. */
export const sanitizeHtmlString = (html: string, sanitizer: DomSanitizer): string => {
    if (!html) {
        return '';
    }

    const stripped = stripMalwareFromHtml(String(html));
    const { html: protectedHtml, blocks } = extractSafeMappingBlocks(stripped);
    const angularSanitized = sanitizer.sanitize(SecurityContext.HTML, protectedHtml) ?? '';
    return restoreSafeMappingBlocks(angularSanitized, blocks);
};

export interface SandboxPreviewOptions {
    cssLink?: string;
    extraHeadHtml?: string;
}

/** Permissive CSP for sandboxed email previews — blocks scripts, allows HTTPS assets. */
export const buildPreviewCspMetaTag = (): string =>
    `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'none'; object-src 'none'; frame-src 'none'; base-uri 'none'; form-action 'none'; connect-src 'none'; style-src 'unsafe-inline' https:; img-src https: data: blob:; font-src https: data:;">`;

/** Build a full HTML document for sandboxed blob iframe preview. */
export const buildSandboxPreviewDocument = (content: string, options: SandboxPreviewOptions = {}): string => {
    const csp = buildPreviewCspMetaTag();
    const cssLink = options.cssLink ? `<link rel="stylesheet" href="${options.cssLink}">` : '';
    const extra = options.extraHeadHtml ?? '';

    if (/<head[\s>]/i.test(content)) {
        return content.replace(/<head([^>]*)>/i, `<head$1>${csp}${cssLink}${extra}`);
    }

    return `<!DOCTYPE html><html><head><meta charset="utf-8">${csp}${cssLink}${extra}</head><body>${content}</body></html>`;
};

export const createSandboxPreviewBlobUrl = (
    html: string,
    sanitizer: DomSanitizer,
    options: SandboxPreviewOptions = {}
): { url: string; safeUrl: SafeResourceUrl } => {
    const sanitized = stripMalwareFromHtml(html);
    const documentHtml = buildSandboxPreviewDocument(sanitized, options);
    const blob = new Blob([documentHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    return { url, safeUrl: sanitizer.bypassSecurityTrustResourceUrl(url) };
};

/** Normalize email log/API body which may be raw HTML or `{ data, type }`. */
export const resolveEmailHtmlBody = (body: string | { data?: string } | null | undefined): string => {
    if (!body) {
        return '';
    }
    return typeof body === 'string' ? body : (body.data ?? '');
};

/** @deprecated Use sanitizeHtmlContent */
export const sanitizeTemplateHtml = sanitizeHtmlContent;
